import { PLATFORM_MODULE_REGISTRY } from './platform-module.registry.js';
/**
 * Canonical capability closure for the first product release.
 *
 * The application is presented as one portfolio-management product. The module
 * identifiers remain internal capability and entitlement metadata; they are
 * not separate products or top-level UI choices.
 *
 * ACCOUNTING and REGULATORY are included as report-support capabilities. Their
 * unrelated operational Data/Admin workspaces remain structurally hidden by
 * the effective route and hub registries.
 */
export const PORTFOLIO_MANAGEMENT_V1_MODULES = [
    'AUTH',
    'DASHBOARD',
    'SETTINGS',
    'NAVIGATION',
    'INVESTMENT',
    'DATA',
    'RECONCILIATION',
    'CORPORATE_ACTIONS',
    'IMPORT',
    'INSTRUMENTS',
    'MARKET_DATA',
    'HOLDINGS',
    'REPORTING',
    'ACCOUNTING',
    'REGULATORY',
    'ADMIN',
    'TAX',
];
/**
 * Commercial grants required for the released PMS capability closure. Global
 * infrastructure capabilities such as AUTH/NAVIGATION/SETTINGS/ADMIN remain
 * available through platform policy and are not separate billable licenses.
 */
export const PORTFOLIO_MANAGEMENT_V1_LICENSABLE_MODULES = Object.freeze(PORTFOLIO_MANAGEMENT_V1_MODULES.filter((moduleId) => PLATFORM_MODULE_REGISTRY[moduleId].licensable));
/** Capabilities retained in code but structurally hidden from the first release. */
export const PORTFOLIO_MANAGEMENT_V1_DEFERRED_MODULES = [
    'FACTS',
    'MARGIN_TRADING',
    'ORDER_MANAGEMENT',
];
/** Platform infrastructure is never part of an organization product grant. */
export const PORTFOLIO_MANAGEMENT_V1_FORBIDDEN_MODULES = [
    'PLATFORM',
];
// Existing names remain the canonical integration points used by persona,
// entitlement, seed, and test code. Their closure now represents the V1 product.
export const INVESTMENT_DOMAIN_MODULES = PORTFOLIO_MANAGEMENT_V1_MODULES;
export const INVESTMENT_DOMAIN_OPTIONAL_MODULES = PORTFOLIO_MANAGEMENT_V1_DEFERRED_MODULES;
export const INVESTMENT_DOMAIN_FORBIDDEN_MODULES = PORTFOLIO_MANAGEMENT_V1_FORBIDDEN_MODULES;
export function isInvestmentDomainModule(module) {
    return INVESTMENT_DOMAIN_MODULES.includes(module);
}
/**
 * Preserve the established stable identifier. Only the descriptive metadata and
 * capability closure change; existing persisted references remain valid.
 */
export const PRODUCT_DOMAIN_IDS = ['investment-domain'];
export const PRODUCT_DOMAIN_REGISTRY = {
    'investment-domain': {
        id: 'investment-domain',
        name: 'Portfolio Management System',
        description: 'Single portfolio-management product composed from existing investment, data, holdings, reference-data, reporting, compliance-reporting, and administration capabilities.',
        uiVisible: false,
        moduleIds: PORTFOLIO_MANAGEMENT_V1_MODULES,
        optionalModuleIds: PORTFOLIO_MANAGEMENT_V1_DEFERRED_MODULES,
        forbiddenModuleIds: PORTFOLIO_MANAGEMENT_V1_FORBIDDEN_MODULES,
    },
};
export function getProductDomain(productDomainId) {
    return PRODUCT_DOMAIN_REGISTRY[productDomainId];
}
export function expandProductDomains(productDomainIds) {
    const selectedModules = new Set();
    for (const productDomainId of productDomainIds) {
        for (const moduleId of getProductDomain(productDomainId).moduleIds) {
            selectedModules.add(moduleId);
        }
    }
    return Object.keys(PLATFORM_MODULE_REGISTRY).filter((moduleId) => selectedModules.has(moduleId));
}
export function validateProductDomainRegistry() {
    for (const [registryKey, descriptor] of Object.entries(PRODUCT_DOMAIN_REGISTRY)) {
        if (registryKey !== descriptor.id) {
            throw new Error(`Product domain registry key "${registryKey}" does not match id "${descriptor.id}"`);
        }
        const included = new Set();
        for (const moduleId of descriptor.moduleIds) {
            if (!(moduleId in PLATFORM_MODULE_REGISTRY)) {
                throw new Error(`Product domain "${descriptor.id}" contains unknown module "${moduleId}"`);
            }
            if (included.has(moduleId)) {
                throw new Error(`Product domain "${descriptor.id}" contains duplicate module "${moduleId}"`);
            }
            included.add(moduleId);
        }
        for (const moduleId of [...descriptor.optionalModuleIds, ...descriptor.forbiddenModuleIds]) {
            if (!(moduleId in PLATFORM_MODULE_REGISTRY)) {
                throw new Error(`Product domain "${descriptor.id}" references unknown module "${moduleId}"`);
            }
            if (included.has(moduleId)) {
                throw new Error(`Product domain "${descriptor.id}" both includes and excludes module "${moduleId}"`);
            }
        }
    }
}
